/**
 * Descriptor-driven Small UPS Summary source (#997 — Story 4.5).
 *
 * Reproduces the bespoke `getSmallUpsEditorSummaryDefinitions()` +
 * `getSmallUpsNoteSummaryDefinitions()` row sets and per-unit value / title
 * projection from the Small UPS descriptor + the shared Summary factory,
 * byte-identically against the Story 4.5 golden masters
 * (`small-ups-summary-rows.json`, `small-ups-summary-filled.json`). This is
 * the engine-driven replacement Story 4.5 re-points the Summary and the golden
 * tests at — same oracle, new implementation.
 *
 * Row-set layout mirrors what the bespoke code produced from
 * `requestFieldRegistry.ts`:
 *
 *   - Per-unit editor group (`subsectionId: 'requirements'` — matches
 *     `smallUpsEditorSummaryRows` at `requestFieldRegistry.ts:290`): `name`,
 *     `status`, then the 15 requirements fields (`smallUpsManufacturer`,
 *     `smallUpsModel`, `powerSmallUpsDescription`, `smallUpsSerialNumber`,
 *     `powerTypesOfUps`, `smallUpsInputVoltage`, `smallUpsInputPlug`,
 *     `powerInputPowerPhase`, `smallUpsCapacity`, `smallUpsLoadPercentage`,
 *     `powerRuntime`, `smallUpsBatteryCabinetQty`, `smallUpsMounting`,
 *     `smallUpsOutlets`, `smallUpsBackupGenerator`) — the `subsection`
 *     marker stamps on the first row (via `buildEditorSubsectionSummaryRows`).
 *   - Per-unit note group (`subsectionId: 'additionalInformation'`):
 *     `powerSmallUpsAdditionalInfo` — marker stamps here. No `labelOverrides`
 *     needed: the SMALL_UPS_FIELDS registry entry for
 *     `powerSmallUpsAdditionalInfo` already carries the label
 *     "Additional Small UPS Info" (contrast PD, where the label had to be
 *     overridden because the FACILITY_POWER_FIELDS registry entry has a
 *     generic label).
 *
 * Small UPS has NO request-level shared field (contrast Liquid Cooling's
 * `lcLiquidToChipDesired`; mirrors Large UPS and Power Distribution), so
 * `sharedFieldKeys` is empty on the descriptor and there is no shared-row
 * accessor here.
 *
 * The identity fields `name`/`status` are not registry fields, so their labels
 * fall back to the editor-slice `shortLabel` values ("Name" / "Type") supplied
 * by `SMALL_UPS_EDITOR_SUBSECTION_FIELDS`. No `labelOverrides` are needed for
 * them (the fallback is already correct).
 */

import { SMALL_UPS_DESCRIPTOR } from './smallUps.descriptor';
import { repeatableInstanceNameTypeRowValue } from '../../summary/repeatableInstanceSummaryDisplay';
import { buildEditorSubsectionSummaryRows } from '../fields/projections/editorSubsectionSummaryRows';
import { smallUpsSummaryRowValue } from '../fields/projections/summaryRowValues';
import {
  SMALL_UPS_EDITOR_SUBSECTION_FIELDS,
  SMALL_UPS_NOTES_SUBSECTION_FIELDS,
} from '../fields/requestFieldRegistry';
import { SMALL_UPS_FIELDS } from '../fields/steps/small-ups.fields';

import type { SummaryCardRow } from '../../summary/summaryCardSourceTypes';
import type { SmallUpsConfiguration } from '../../types/smallUps';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';

const RBAC_ANCHOR = SMALL_UPS_DESCRIPTOR.permissionRef.rbacAnchorDataField;
const NOTE_RBAC_ANCHOR = 'powerSmallUpsAdditionalInfo';

/**
 * Registry field metadata (labels) the factory resolves per field. Keeping
 * this pointed at the SMALL_UPS_FIELDS registry (rather than the descriptor's
 * `fields`) mirrors the bespoke source and keeps labels/subsection metadata
 * flowing through the same lookup the engine expects.
 */
const REGISTRY_FIELDS = SMALL_UPS_FIELDS;

function buildGroup(
  editorFields: readonly SubsectionFieldDescriptor[],
  subsectionId: string,
  rbacAnchorDataField: string,
  labelOverrides?: Readonly<Record<string, string>>,
): SummaryCardRow[] {
  return buildEditorSubsectionSummaryRows({
    editorFields,
    registryFields: REGISTRY_FIELDS,
    rbacAnchorDataField,
    subsectionId,
    labelOverrides,
  });
}

/**
 * Full ordered per-unit Small UPS Summary row set (editor + note),
 * byte-identical to the bespoke `getPerSmallUpsSummaryDefinitions()` output
 * (which concatenates `getSmallUpsEditorSummaryDefinitions()` +
 * `getSmallUpsNoteSummaryDefinitions()`).
 */
export function getSmallUpsDescriptorSummaryDefinitions(): SummaryCardRow[] {
  return [
    ...buildGroup(
      SMALL_UPS_EDITOR_SUBSECTION_FIELDS,
      'requirements',
      RBAC_ANCHOR,
    ),
    ...buildGroup(
      SMALL_UPS_NOTES_SUBSECTION_FIELDS,
      'additionalInformation',
      NOTE_RBAC_ANCHOR,
    ),
  ];
}

/**
 * Per-unit Small UPS Summary value — delegates to
 * `repeatableInstanceNameTypeRowValue` for the identity fields (`name`,
 * Existing/New `status`) and `smallUpsSummaryRowValue` for the SU detail
 * fields (handles the numeric string coercion for `smallUpsCapacity` /
 * `smallUpsLoadPercentage` / `smallUpsBatteryCabinetQty` and the Yes/No
 * projection for `smallUpsBackupGenerator`). Re-homed here so the Summary
 * value path no longer routes through the bespoke display module.
 */
export function smallUpsDescriptorUnitRowValue(
  unit: SmallUpsConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  const nameTypeValue = repeatableInstanceNameTypeRowValue(unit, dataField);
  if (nameTypeValue !== undefined) {
    return nameTypeValue;
  }
  return smallUpsSummaryRowValue(
    unit as unknown as Record<string, unknown>,
    dataField,
  );
}

/**
 * Resolve a Small UPS unit's display name (user-set `name` → `Small UPS N`
 * fallback based on `displayOrder`, else `null` so the caller falls back to
 * the ordinal). Kept here so the Summary title no longer depends on the
 * bespoke display module.
 */
function resolveSmallUpsSummaryDisplayName(
  unit: SmallUpsConfiguration,
): string | null {
  const name = unit.name?.trim();
  if (name) {
    return name;
  }
  if (unit.displayOrder != null && unit.displayOrder > 0) {
    return `Small UPS ${unit.displayOrder}`;
  }
  return null;
}

/**
 * Per-unit Summary card title — `Existing/New — <name>` (fallback
 * `Existing/New Small UPS N`).
 */
export function getSmallUpsDescriptorUnitTitle(
  unit: SmallUpsConfiguration,
  index: number,
): string {
  const status = unit.status === 'existing' ? 'Existing' : 'New';
  const name = resolveSmallUpsSummaryDisplayName(unit);
  return name ? `${status} — ${name}` : `${status} Small UPS ${index + 1}`;
}
